// ============= Imports =============
import { AuthContextProps } from '@/contexts/AuthContext';
import { tokenManager } from '@tw/tw-component-library';

// ============= Constants =============
const BASE_URL = import.meta.env.VITE_BASE_DATA_URL;
const API_ENDPOINT = '/api/Document/workspace';
const LITIUM_SCOPE = import.meta.env.VITE_LITIUM_DATA_SCOPE;

const HEADERS = {
  'Content-Type': 'application/json',
};

// ============= Types =============

export interface CollectionSuggestion {
  collectionName: string;
  description: string;
  collectionId?: string; // If suggesting an existing collection
  confidence: number; // 0-1 confidence score
  reason?: string; // Why this suggestion was made
}

export interface GetCollectionSuggestionsProps {
  getAccessToken: AuthContextProps['getAccessToken'];
  workspaceId: string;
  file: File;
  existingCollections?: Array<{
    id: string;
    name: string;
    description: string;
  }>;
}

// ============= Utility Functions =============
/**
 * Creates headers for API request
 * @param token Authentication token
 * @returns Headers object
 */
const createHeaders = (token: string) => ({
  Authorization: `Bearer ${token}`,
  ...HEADERS,
});

// ============= Main Function =============
/**
 * Gets AI-powered collection suggestions for a document
 * Analyzes the document content and suggests collection names or existing collections
 * @param getAccessToken Function to acquire access token
 * @param workspaceId Workspace identifier
 * @param file The file to analyze
 * @param existingCollections Optional list of existing collections to match against
 * @returns Promise with collection suggestions
 */
export const getCollectionSuggestions = async ({
  getAccessToken,
  workspaceId,
  file,
  existingCollections = [],
}: GetCollectionSuggestionsProps): Promise<CollectionSuggestion[]> => {
  // TODO: Replace with actual API call when backend is ready
  console.log('🤖 Getting AI collection suggestions (MOCK MODE)...');

  // Simulate API delay
  await new Promise((resolve) => setTimeout(resolve, 800));

  return getFallbackSuggestions(file, existingCollections);

  /* 
  // UNCOMMENT THIS WHEN BACKEND IS READY:
  try {
    console.log('🤖 Getting AI collection suggestions...');

    // Token acquisition with required scopes
    const token = await getAccessToken([LITIUM_SCOPE]);
    if (!token) {
      throw new Error('Failed to acquire API token');
    }
    tokenManager.logTokenInfo(token, 'API');

    // Create FormData with file
    const formData = new FormData();
    formData.append('file', file);
    
    // Add existing collections info if provided
    if (existingCollections.length > 0) {
      formData.append(
        'existingCollections',
        JSON.stringify(existingCollections)
      );
    }

    // URL construction
    const url = `${BASE_URL}${API_ENDPOINT}/${workspaceId}/suggestions/collections`;
    const convertURI = encodeURI(url);
    console.log('🌐 Making request to:', convertURI);

    // API request
    const response = await fetch(convertURI, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        // Don't set Content-Type for FormData - browser will set it with boundary
      },
      body: formData,
    });

    // Response handling
    if (!response.ok) {
      // If endpoint doesn't exist yet, return mock suggestions based on file name
      if (response.status === 404) {
        console.log('⚠️ Suggestions endpoint not available, using fallback logic');
        return getFallbackSuggestions(file, existingCollections);
      }
      const errorText = await response.text();
      console.log(`HTTP error! status: ${response.status}`, errorText);
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    console.log(`✅ Collection suggestions received:`, data);

    // Handle different response formats
    if (Array.isArray(data)) {
      return data;
    } else if (data.suggestions && Array.isArray(data.suggestions)) {
      return data.suggestions;
    } else if (data.data && Array.isArray(data.data)) {
      return data.data;
    }

    return [];
  } catch (error) {
    console.error('🔴 Error getting collection suggestions:', error);
    // Return fallback suggestions on error
    return getFallbackSuggestions(file, existingCollections);
  }
  */
};

/**
 * Fallback function to generate basic suggestions based on file name
 * This is used when the API endpoint is not available
 * Currently enhanced for MOCK MODE to provide realistic suggestions
 */
const getFallbackSuggestions = (
  file: File,
  existingCollections: Array<{ id: string; name: string; description: string }>
): CollectionSuggestion[] => {
  const suggestions: CollectionSuggestion[] = [];
  const fileName = file.name.toLowerCase();
  const fileExtension = file.name.split('.').pop()?.toLowerCase() || '';

  // Extract keywords from filename
  const keywords = fileName
    .replace(/[^a-z0-9\s]/g, ' ')
    .split(/\s+/)
    .filter((word) => word.length > 3);

  // Try to match existing collections with better matching logic
  existingCollections.forEach((collection) => {
    const collectionNameLower = collection.name.toLowerCase();

    // Check for exact keyword matches
    const exactMatch = keywords.some(
      (keyword) =>
        collectionNameLower.includes(keyword) ||
        keyword.includes(collectionNameLower)
    );

    // Check for partial matches
    const partialMatch = keywords.some((keyword) => {
      const keywordStart = keyword.substring(0, 4);
      return (
        collectionNameLower.includes(keywordStart) ||
        collectionNameLower
          .split(/\s+/)
          .some((word) => word.startsWith(keywordStart))
      );
    });

    let matchScore = 0;
    let reason = '';

    if (exactMatch) {
      matchScore = 0.85;
      reason = `Strong match: file name keywords match collection name`;
    } else if (partialMatch) {
      matchScore = 0.65;
      reason = `Partial match: similar keywords found in collection name`;
    } else if (
      collectionNameLower.includes('workflow') &&
      fileName.includes('workflow')
    ) {
      matchScore = 0.75;
      reason = `Workflow-related document matches workflow collection`;
    }

    if (matchScore > 0.5) {
      suggestions.push({
        collectionName: collection.name,
        description: collection.description,
        collectionId: collection.id,
        confidence: matchScore,
        reason,
      });
    }
  });

  // Generate new collection name suggestions based on file patterns
  const patterns = [
    {
      keywords: ['invoice', 'bill', 'billing', 'payment'],
      name: 'Invoices & Billing',
      description: 'Documents related to invoices and billing',
      confidence: 0.85,
      reason: 'Document appears to be an invoice or billing document',
    },
    {
      keywords: ['contract', 'agreement', 'mou', 'nda'],
      name: 'Contracts & Agreements',
      description: 'Documents related to contracts and agreements',
      confidence: 0.85,
      reason: 'Document appears to be a contract or legal agreement',
    },
    {
      keywords: ['report', 'analysis', 'summary'],
      name: 'Reports & Analysis',
      description: 'Documents related to reports and analysis',
      confidence: 0.75,
      reason: 'Document appears to be a report or analysis',
    },
    {
      keywords: ['receipt', 'expense', 'reimbursement'],
      name: 'Receipts & Expenses',
      description: 'Documents related to receipts and expenses',
      confidence: 0.8,
      reason: 'Document appears to be a receipt or expense record',
    },
    {
      keywords: ['proposal', 'quote', 'estimate'],
      name: 'Proposals & Quotes',
      description: 'Documents related to proposals and quotes',
      confidence: 0.8,
      reason: 'Document appears to be a proposal or quote',
    },
    {
      keywords: ['meeting', 'minutes', 'notes'],
      name: 'Meeting Notes',
      description: 'Documents related to meeting notes and minutes',
      confidence: 0.75,
      reason: 'Document appears to be meeting notes or minutes',
    },
    {
      keywords: ['tax', 'irs', 'w2', '1099'],
      name: 'Tax Documents',
      description: 'Documents related to tax documents',
      confidence: 0.9,
      reason: 'Document appears to be tax-related',
    },
    {
      keywords: ['legal', 'court', 'litigation', 'lawsuit'],
      name: 'Legal Documents',
      description: 'Documents related to legal documents',
      confidence: 0.85,
      reason: 'Document appears to be legal-related',
    },
    {
      keywords: ['financial', 'statement', 'balance', 'income'],
      name: 'Financial Statements',
      description: 'Documents related to financial statements',
      confidence: 0.8,
      reason: 'Document appears to be a financial statement',
    },
    {
      keywords: ['hr', 'human', 'resource', 'employee', 'personnel'],
      name: 'HR Documents',
      description: 'Documents related to HR documents',
      confidence: 0.8,
      reason: 'Document appears to be HR or personnel-related',
    },
  ];

  // Check for pattern matches
  for (const pattern of patterns) {
    if (pattern.keywords.some((keyword) => fileName.includes(keyword))) {
      // Check if we already suggested this as an existing collection
      const alreadySuggested = suggestions.some((s) =>
        s.collectionName
          .toLowerCase()
          .includes(pattern.name.toLowerCase().split(' ')[0])
      );

      if (!alreadySuggested) {
        suggestions.push({
          collectionName: pattern.name,
          description: pattern.description,
          confidence: pattern.confidence,
          reason: pattern.reason,
        });
      }
    }
  }

  // If no pattern matches, try to create a suggestion from keywords
  if (suggestions.length === 0 && keywords.length > 0) {
    // Use first significant keyword as collection name
    const firstKeyword =
      keywords[0].charAt(0).toUpperCase() + keywords[0].slice(1);
    suggestions.push({
      collectionName: `${firstKeyword} Documents`,
      description: `${firstKeyword} Documents`,
      confidence: 0.6,
      reason: `Based on file name keyword: ${firstKeyword}`,
    });
  }

  // Add a generic "General Documents" suggestion if we have very few suggestions
  if (suggestions.length < 2) {
    suggestions.push({
      collectionName: 'General Documents',
      description: 'General purpose collection for miscellaneous documents',
      confidence: 0.5,
      reason: 'General purpose collection for miscellaneous documents',
    });
  }

  // Sort by confidence (highest first) and limit to top 5
  return suggestions.sort((a, b) => b.confidence - a.confidence).slice(0, 5);
};
